<script setup>
import { ref, onMounted } from 'vue'

// Local file system state
const localPath = ref('/Users/username/')
const localFiles = ref([])
const localLoading = ref(false)

// Remote file system state
const remotePath = ref('/')
const remoteFiles = ref([])
const remoteLoading = ref(false)
const connected = ref(false)

const connectionSettings = ref({
  type: 'sftp', // 'sftp', 'ftp'
  host: '',
  port: 22,
  username: '',
  password: '',
  keyFile: null
})

const loadLocalFiles = async () => {
  localLoading.value = true
  try {
    // Placeholder local files
    localFiles.value = [
      { name: '..', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'Documents', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'Downloads', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'Desktop', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'config.json', type: 'file', size: '1.2 KB', modified: '2025-01-01' },
      { name: 'backup.sql', type: 'file', size: '15.3 MB', modified: '2024-12-31' },
      { name: 'README.md', type: 'file', size: '2.1 KB', modified: '2024-12-30' }
    ]
  } catch (error) {
    console.error('Failed to load local files:', error)
  } finally {
    localLoading.value = false
  }
}

const loadRemoteFiles = async () => {
  if (!connected.value) return
  
  remoteLoading.value = true
  try {
    // Placeholder remote files
    remoteFiles.value = [
      { name: '..', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'var', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'etc', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'home', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'tmp', type: 'directory', size: '-', modified: '2025-01-01' },
      { name: 'app.log', type: 'file', size: '5.2 MB', modified: '2025-01-01' },
      { name: 'database.sql', type: 'file', size: '45.3 MB', modified: '2024-12-31' }
    ]
  } catch (error) {
    console.error('Failed to load remote files:', error)
  } finally {
    remoteLoading.value = false
  }
}

const navigateLocal = (path) => {
  if (path === '..') {
    const parts = localPath.value.split('/').filter(Boolean)
    parts.pop()
    localPath.value = '/' + parts.join('/') + '/'
  } else {
    localPath.value = localPath.value + path + '/'
  }
  loadLocalFiles()
}

const navigateRemote = (path) => {
  if (path === '..') {
    const parts = remotePath.value.split('/').filter(Boolean)
    parts.pop()
    remotePath.value = '/' + parts.join('/') + '/'
  } else {
    remotePath.value = remotePath.value + path + '/'
  }
  loadRemoteFiles()
}

const connectToServer = () => {
  connected.value = true
  loadRemoteFiles()
}

const disconnect = () => {
  connected.value = false
  remoteFiles.value = []
}

onMounted(() => {
  loadLocalFiles()
})
</script>

<template>
  <div class="max-w-full h-full flex flex-col">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">S/FTP File Manager</h1>
      <p class="text-gray-600">Manage local and remote files via S/FTP connections</p>
    </div>

    <!-- Connection Settings -->
    <div class="bg-white rounded-lg shadow-sm border p-4 mb-4">
      <h2 class="text-lg font-semibold mb-3">Remote Server Connection</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Protocol</label>
          <select v-model="connectionSettings.type" class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm">
            <option value="sftp">SFTP</option>
            <option value="ftp">FTP</option>
          </select>
        </div>
        
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Host</label>
          <input v-model="connectionSettings.host" type="text" class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" placeholder="example.com">
        </div>
        
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Port</label>
          <input v-model="connectionSettings.port" type="number" class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" :placeholder="connectionSettings.type === 'sftp' ? '22' : '21'">
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Username</label>
          <input v-model="connectionSettings.username" type="text" class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm">
        </div>
        
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Password</label>
          <input v-model="connectionSettings.password" type="password" class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm">
        </div>
      </div>

      <div class="flex gap-2">
        <button @click="connectToServer" :disabled="connected" class="bg-pink-500 text-white px-3 py-1 rounded-md hover:bg-pink-600 disabled:opacity-50 text-sm">
          <i class="fas fa-plug mr-1"></i>
          {{ connected ? 'Connected' : 'Connect' }}
        </button>
        <button @click="disconnect" :disabled="!connected" class="bg-gray-500 text-white px-3 py-1 rounded-md hover:bg-gray-600 disabled:opacity-50 text-sm">
          <i class="fas fa-times mr-1"></i>
          Disconnect
        </button>
        <div class="flex items-center ml-4">
          <div :class="connected ? 'bg-green-500' : 'bg-red-500'" class="w-2 h-2 rounded-full mr-2"></div>
          <span class="text-sm text-gray-600">{{ connected ? 'Connected' : 'Disconnected' }}</span>
        </div>
      </div>
    </div>

    <!-- Dual Pane File Browser -->
    <div class="flex-1 grid grid-cols-2 gap-4 min-h-0">
      <!-- Local Files Panel -->
      <div class="bg-white rounded-lg shadow-sm border flex flex-col">
        <div class="p-3 border-b border-gray-200 bg-blue-50">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <i class="fas fa-desktop text-blue-600 mr-2"></i>
              <h3 class="font-medium text-blue-800">Local Files</h3>
            </div>
            <div class="flex items-center space-x-1">
              <button @click="loadLocalFiles" class="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs">
                <i class="fas fa-sync-alt"></i>
              </button>
              <button class="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs">
                <i class="fas fa-folder-plus"></i>
              </button>
              <button class="px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs">
                <i class="fas fa-upload"></i>
              </button>
            </div>
          </div>
          <div class="mt-2">
            <span class="text-xs text-blue-700 font-mono">{{ localPath }}</span>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div v-if="localLoading" class="text-center py-8">
            <i class="fas fa-spinner fa-spin text-blue-400 text-xl"></i>
            <p class="text-blue-500 mt-2 text-sm">Loading local files...</p>
          </div>

          <div v-else class="p-2">
            <div class="grid grid-cols-12 gap-2 py-1 px-2 text-xs font-medium text-gray-500 border-b">
              <div class="col-span-6">Name</div>
              <div class="col-span-3">Size</div>
              <div class="col-span-3">Modified</div>
            </div>
            
            <div v-for="file in localFiles" :key="file.name" 
                 class="grid grid-cols-12 gap-2 py-1 px-2 hover:bg-blue-50 rounded cursor-pointer text-xs"
                 @click="file.type === 'directory' ? navigateLocal(file.name) : null">
              <div class="col-span-6 flex items-center">
                <i :class="file.type === 'directory' ? 'fas fa-folder text-yellow-500' : 'fas fa-file text-gray-400'" class="mr-1"></i>
                <span class="truncate">{{ file.name }}</span>
              </div>
              <div class="col-span-3 text-gray-600">{{ file.size }}</div>
              <div class="col-span-3 text-gray-600">{{ file.modified }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Remote Files Panel -->
      <div class="bg-white rounded-lg shadow-sm border flex flex-col">
        <div class="p-3 border-b border-gray-200 bg-pink-50">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <i class="fas fa-server text-pink-600 mr-2"></i>
              <h3 class="font-medium text-pink-800">Remote Server</h3>
            </div>
            <div class="flex items-center space-x-1">
              <button @click="loadRemoteFiles" :disabled="!connected" class="px-2 py-1 bg-pink-100 text-pink-700 rounded hover:bg-pink-200 disabled:opacity-50 text-xs">
                <i class="fas fa-sync-alt"></i>
              </button>
              <button :disabled="!connected" class="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 text-xs">
                <i class="fas fa-folder-plus"></i>
              </button>
              <button :disabled="!connected" class="px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50 text-xs">
                <i class="fas fa-download"></i>
              </button>
            </div>
          </div>
          <div class="mt-2">
            <span class="text-xs text-pink-700 font-mono">{{ connected ? remotePath : 'Not connected' }}</span>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div v-if="!connected" class="text-center py-12 text-gray-400">
            <i class="fas fa-plug text-3xl mb-3"></i>
            <p>Connect to server to browse remote files</p>
          </div>

          <div v-else-if="remoteLoading" class="text-center py-8">
            <i class="fas fa-spinner fa-spin text-pink-400 text-xl"></i>
            <p class="text-pink-500 mt-2 text-sm">Loading remote files...</p>
          </div>

          <div v-else class="p-2">
            <div class="grid grid-cols-12 gap-2 py-1 px-2 text-xs font-medium text-gray-500 border-b">
              <div class="col-span-6">Name</div>
              <div class="col-span-3">Size</div>
              <div class="col-span-3">Modified</div>
            </div>
            
            <div v-for="file in remoteFiles" :key="file.name" 
                 class="grid grid-cols-12 gap-2 py-1 px-2 hover:bg-pink-50 rounded cursor-pointer text-xs"
                 @click="file.type === 'directory' ? navigateRemote(file.name) : null">
              <div class="col-span-6 flex items-center">
                <i :class="file.type === 'directory' ? 'fas fa-folder text-yellow-500' : 'fas fa-file text-gray-400'" class="mr-1"></i>
                <span class="truncate">{{ file.name }}</span>
              </div>
              <div class="col-span-3 text-gray-600">{{ file.size }}</div>
              <div class="col-span-3 text-gray-600">{{ file.modified }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer Controls -->
    <div class="mt-4 bg-gray-50 rounded-lg p-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <button :disabled="!connected" class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">
            <i class="fas fa-arrow-right mr-1"></i>
            Upload to Server
          </button>
          <button :disabled="!connected" class="px-3 py-1 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 text-sm">
            <i class="fas fa-arrow-left mr-1"></i>
            Download to Local
          </button>
        </div>
        <div class="text-xs text-gray-500">
          Select files and use transfer buttons (functions not implemented)
        </div>
      </div>
    </div>
  </div>
</template> 